const ALLOWED_TAGS = new Set([
  "a",
  "b",
  "blockquote",
  "br",
  "caption",
  "code",
  "div",
  "em",
  "figcaption",
  "figure",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "hr",
  "i",
  "img",
  "li",
  "ol",
  "p",
  "pre",
  "s",
  "small",
  "span",
  "strong",
  "sub",
  "sup",
  "table",
  "tbody",
  "td",
  "tfoot",
  "th",
  "thead",
  "tr",
  "u",
  "ul",
]);

const DROP_WITH_CONTENT_TAGS = new Set([
  "base",
  "button",
  "embed",
  "form",
  "iframe",
  "input",
  "link",
  "meta",
  "object",
  "script",
  "select",
  "style",
  "svg",
  "template",
  "textarea",
]);

const ALLOWED_GLOBAL_ATTRIBUTES = new Set([
  "class",
  "role",
  "style",
  "title",
]);

const ALLOWED_TAG_ATTRIBUTES = {
  a: new Set(["href", "rel", "target"]),
  img: new Set(["alt", "height", "loading", "src", "width"]),
  table: new Set(["summary"]),
  td: new Set(["colspan", "rowspan"]),
  th: new Set(["colspan", "rowspan", "scope"]),
};

const ALLOWED_CSS_PROPERTIES = new Set([
  "align-items",
  "background",
  "background-color",
  "border",
  "border-bottom",
  "border-color",
  "border-left",
  "border-radius",
  "border-right",
  "border-style",
  "border-top",
  "border-width",
  "color",
  "display",
  "flex",
  "flex-direction",
  "flex-wrap",
  "font-family",
  "font-size",
  "font-style",
  "font-weight",
  "gap",
  "grid-template-columns",
  "height",
  "justify-content",
  "letter-spacing",
  "line-height",
  "margin",
  "margin-bottom",
  "margin-left",
  "margin-right",
  "margin-top",
  "max-height",
  "max-width",
  "min-height",
  "min-width",
  "overflow",
  "overflow-wrap",
  "overflow-x",
  "overflow-y",
  "padding",
  "padding-bottom",
  "padding-left",
  "padding-right",
  "padding-top",
  "text-align",
  "text-decoration",
  "text-transform",
  "white-space",
  "width",
  "word-break",
]);

const DANGEROUS_CSS_VALUE_PATTERN =
  /(?:expression\s*\(|javascript\s*:|url\s*\(|@import|behavior\s*:|-moz-binding)/i;

const sanitizeStyle = (styleValue) =>
  String(styleValue || "")
    .split(";")
    .map((declaration) => declaration.trim())
    .filter(Boolean)
    .map((declaration) => {
      const separatorIndex = declaration.indexOf(":");
      if (separatorIndex < 1) return "";

      const property = declaration.slice(0, separatorIndex).trim().toLowerCase();
      const value = declaration.slice(separatorIndex + 1).trim();

      if (
        !ALLOWED_CSS_PROPERTIES.has(property) ||
        !value ||
        DANGEROUS_CSS_VALUE_PATTERN.test(value)
      ) {
        return "";
      }

      return `${property}: ${value}`;
    })
    .filter(Boolean)
    .join("; ");

const isSafeUrl = (value, attributeName) => {
  const normalizedValue = String(value || "").trim();
  if (!normalizedValue) return false;

  if (
    attributeName === "src" &&
    /^data:image\/(?:png|gif|jpe?g|webp);base64,/i.test(normalizedValue)
  ) {
    return true;
  }

  try {
    const url = new URL(normalizedValue, window.location.origin);
    const allowedProtocols =
      attributeName === "href"
        ? new Set(["http:", "https:", "mailto:", "tel:"])
        : new Set(["http:", "https:"]);

    return allowedProtocols.has(url.protocol);
  } catch {
    return false;
  }
};

const sanitizeElementAttributes = (element) => {
  const tagName = element.tagName.toLowerCase();
  const tagAttributes = ALLOWED_TAG_ATTRIBUTES[tagName] || new Set();

  Array.from(element.attributes).forEach((attribute) => {
    const attributeName = attribute.name.toLowerCase();
    const isAriaAttribute = attributeName.startsWith("aria-");
    const isAllowed =
      ALLOWED_GLOBAL_ATTRIBUTES.has(attributeName) ||
      tagAttributes.has(attributeName) ||
      isAriaAttribute;

    if (!isAllowed || attributeName.startsWith("on")) {
      element.removeAttribute(attribute.name);
      return;
    }

    if (
      (attributeName === "href" || attributeName === "src") &&
      !isSafeUrl(attribute.value, attributeName)
    ) {
      element.removeAttribute(attribute.name);
      return;
    }

    if (attributeName === "style") {
      const safeStyle = sanitizeStyle(attribute.value);
      if (safeStyle) {
        element.setAttribute("style", safeStyle);
      } else {
        element.removeAttribute("style");
      }
    }

    if (
      attributeName === "target" &&
      !["_blank", "_self"].includes(attribute.value)
    ) {
      element.removeAttribute("target");
    }
  });

  if (tagName === "a" && element.getAttribute("target") === "_blank") {
    element.setAttribute("rel", "noopener noreferrer");
  }
};

const sanitizeHtml = (html) => {
  if (!html || typeof window === "undefined") return "";

  const documentNode = new DOMParser().parseFromString(
    String(html),
    "text/html"
  );

  Array.from(documentNode.body.querySelectorAll("*")).forEach((element) => {
    const tagName = element.tagName.toLowerCase();

    if (DROP_WITH_CONTENT_TAGS.has(tagName)) {
      element.remove();
      return;
    }

    if (!ALLOWED_TAGS.has(tagName)) {
      element.replaceWith(...Array.from(element.childNodes));
      return;
    }

    sanitizeElementAttributes(element);
  });

  return documentNode.body.innerHTML;
};

export default sanitizeHtml;
